import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Grid } from '@material-ui/core'
import { Alert, Loader, Toast } from '@refera/ui-web'
import { navigate, useParams } from '@reach/router'
import { useSelector, useDispatch } from 'react-redux'
import WarningIcon from '@mui/icons-material/Warning'

import HeaderTitle from '_components/header-title'
import ModalDialog from '_components/modal/modal-dialog'
import ProcedureOverview from '_components/help-requests/procedure-overview'
import ProcedureTable from '_components/help-requests/procedure-table'
import useRolePermission from '_hooks/use-role-permission'
import useHelpRequestReadOnly, {
  INTERMEDIARY_READ_ONLY_ALERT_DESCRIPTION,
} from '_hooks/use-help-request-read-only'
import { ROUTES } from '_utils/constants'
import {
  getHelpRequestById,
  getHelpRequestPendings,
  clearFeedbackMessage,
} from '_modules/helpRequests/actions'
import {
  getHelpRequestsByIDSelector,
  getFeedbackMessageSelector,
} from '_modules/helpRequests/selectors'
import { currentServiceOrderSelector } from '_modules/service-orders/selectors'

import useStyles from './styles'

const HelpProceduresScreen = () => {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { id: helpRequestId } = useParams()

  const serviceOrder = useSelector(currentServiceOrderSelector)
  const feedbackMessage = useSelector(getFeedbackMessageSelector)
  const helpRequest = useSelector(getHelpRequestsByIDSelector(helpRequestId))

  const [isLoadingGetHelpRequest, setLoadingGetHelpRequest] = useState(false)

  const { isAdmin, isIntermediary, isTradesman } = useRolePermission()
  const isReadOnly = useHelpRequestReadOnly(helpRequest)
  const [toastMessage, setToastMessage] = useState('')
  const [modalDialog, setModalDialog] = useState({ isOpen: false, subTitle: '' })

  useEffect(() => {
    if (!helpRequest && helpRequestId && !isLoadingGetHelpRequest) {
      setLoadingGetHelpRequest(true)
      dispatch(getHelpRequestById(helpRequestId))
        .then(() => setLoadingGetHelpRequest(false))
        .catch(() => setLoadingGetHelpRequest(false))
    }
  }, [helpRequest, helpRequestId, isLoadingGetHelpRequest])

  useEffect(() => {
    dispatch(getHelpRequestPendings(helpRequestId))
  }, [helpRequestId])

  const handleGoToMovidesk = useCallback(() => {
    if (isAdmin) {
      window.open(
        'https://refera.movidesk.com/kb/article/367569/o-que-pausa-um-chamado',
        '_blank',
        'noopener,noreferrer'
      )
      return
    }

    if (isIntermediary) {
      window.open(
        'https://refera.movidesk.com/kb/article/368897/o-que-e-um-chamado-pausado',
        '_blank',
        'noopener,noreferrer'
      )
      return
    }

    if (isTradesman) {
      window.open(
        'https://refera.movidesk.com/kb/article/367572/o-que-e-um-chamado-pausado',
        '_blank',
        'noopener,noreferrer'
      )
    }
  }, [isAdmin, isIntermediary, isTradesman])

  const handleCloseFeedbackMessage = useCallback(() => {
    dispatch(clearFeedbackMessage())
  }, [dispatch])

  const handleCloseToast = useCallback(() => setToastMessage(''), [])

  const isLoading = useMemo(() => isLoadingGetHelpRequest, [isLoadingGetHelpRequest])

  return (
    <>
      <Loader open={isLoading} hasBackdrop label="Aguarde..." />
      <HeaderTitle title="Trâmites" backButtonAction={() => navigate(ROUTES.HELP_REQUESTS)} />
      <ProcedureOverview isReadOnly={isReadOnly} />
      <Grid className={styles.tableContainer}>
        {isReadOnly && (
          <Alert severity="info" className={styles.readOnlyAlert} title="Somente leitura">
            {INTERMEDIARY_READ_ONLY_ALERT_DESCRIPTION}
          </Alert>
        )}
        {serviceOrder?.isVipClient && (
          <Alert
            severity="warning"
            title="ATENÇÃO: Chamado de um cliente VIP"
            icon={<WarningIcon fontSize="medium" color="warning" />}
          />
        )}
        <ProcedureTable isLoading={isLoading} isReadOnly={isReadOnly} />
        <Toast
          draggable
          open={toastMessage.length > 1}
          autoHideDuration={6000}
          onClose={handleCloseToast}
        >
          <Alert
            severity={toastMessage.includes('sucesso') ? 'success' : 'error'}
            title={toastMessage}
            onClose={handleCloseToast}
          />
        </Toast>

        <Toast
          open={Boolean(feedbackMessage?.length)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          autoHideDuration={5000}
          onClose={handleCloseFeedbackMessage}
        >
          <Alert
            severity="info"
            title={feedbackMessage}
            onClose={handleCloseFeedbackMessage}
            actionTitle="Clique aqui para saber mais"
            action={handleGoToMovidesk}
          />
        </Toast>
      </Grid>
      <ModalDialog modalDialog={modalDialog} setModalDialog={setModalDialog} />
    </>
  )
}

export default HelpProceduresScreen
